/**
 * PlayersPanel
 *
 * Players tab content: active/banned player lists, FPR pre-flight banner,
 * registration badges, and per-player enable/ban/edit actions. Reads player
 * data from the usePlayerManagement hook; opens the registration and
 * seed-players modals via dashboard state callbacks.
 */

import { GameType, gameUsesAvatars, getPlayerAvatarId, getPlayerAvatarTone, type GamePlayerInfo } from '@swtorbit/shared-socket-events';
import { PlayerAvatar } from '@swtorbit/shared-components';
import { getFprRoleIconUrl } from '@swtorbit/shared-assets';
import { useGameContext } from '../../contexts/GameContext';
import type { PlayerManagement } from '../../hooks/usePlayerManagement';

interface PlayersPanelProps {
  playerManagement: PlayerManagement;
  onEditRegistration: (player: GamePlayerInfo) => void;
  onOpenSeedModal: () => void;
}

export function PlayersPanel({ playerManagement, onEditRegistration, onOpenSeedModal }: PlayersPanelProps) {
  const { selectedGame } = useGameContext();
  const {
    players,
    bannedPlayers,
    showBanned,
    setShowBanned,
    refreshPlayers,
    togglePlayer,
    banPlayer,
    unbanPlayer,
  } = playerManagement;

  const isFprGame = selectedGame?.gameType === GameType.FP_RACE;
  const avatarGame = selectedGame && gameUsesAvatars(selectedGame.gameType) ? selectedGame : null;

  return (
    <div className="w-100 align-self-stretch">
      <h2>Game Players {selectedGame ? `- ${selectedGame.name || selectedGame.roomCode}` : ''}</h2>
      {!selectedGame ? (
        <p className="text-center p-2xl text-slate">Select a game from the dropdown above to manage players.</p>
      ) : (
        <>
          {/* FPR pre-flight warning banner */}
          {isFprGame && selectedGame.status === 'team_creation' && (() => {
            const missingReg = players.filter(p => p.enabled !== false && !p.fprRegistration && !p.fprPendingDiscordLink).length;
            const pendingLink = players.filter(p => p.enabled !== false && p.fprPendingDiscordLink).length;
            const missingDiscord = players.filter(p => p.enabled !== false && !p.discordId).length;
            if (missingReg === 0 && pendingLink === 0 && missingDiscord === 0) return null;
            return (
              <div className="bg-space bg-o-50 border-md border-amber rounded-base p-sm mb-sm d-flex gap-xs align-items-center flex-wrap">
                <span className="text-amber fw-bold">⚠ Pre-flight:</span>
                {missingReg > 0 && <span className="text-amber">{missingReg} player{missingReg !== 1 ? 's' : ''} missing registration</span>}
                {missingReg > 0 && (pendingLink > 0 || missingDiscord > 0) && <span className="text-slate">·</span>}
                {pendingLink > 0 && <span className="text-amber">{pendingLink} player{pendingLink !== 1 ? 's' : ''} need Discord link (claimed Discord reg)</span>}
                {pendingLink > 0 && missingDiscord > 0 && <span className="text-slate">·</span>}
                {missingDiscord > 0 && <span className="text-slate">{missingDiscord} player{missingDiscord !== 1 ? 's' : ''} not linked to Discord</span>}
              </div>
            );
          })()}

          <div className="d-flex gap-xs">
            <button onClick={refreshPlayers}>Refresh</button>
            <button
              onClick={() => setShowBanned(!showBanned)}
              className={showBanned ? 'is-active' : ''}
            >
              {showBanned ? 'Show Active' : `Show Banned (${bannedPlayers.length})`}
            </button>
            {selectedGame.debugEnabled && isFprGame && (
              <button
                className="btn btn--outline btn--slate"
                onClick={onOpenSeedModal}
                title="Seed fake players for testing (debug mode only)"
              >
                Seed Test Players
              </button>
            )}
          </div>

          {!showBanned ? (
            <div className="d-flex flex-column gap-xs">
              {players.length === 0 ? (
                <p className="empty-message">No players in this game yet.</p>
              ) : (
                players.map((player) => (
                  <div key={player.playerId} className={`list__item ${!player.enabled ? 'is-disabled' : ''}`}>
                    <div className="player-info" style={{ flex: 1 }}>
                      <div className="d-flex align-items-center gap-xs flex-wrap">
                        <span className={`dot ${player.isConnected ? 'dot--green' : 'dot--slate'}`} />
                        {avatarGame && (
                          <PlayerAvatar
                            avatarId={getPlayerAvatarId(avatarGame.players, player.playerId)}
                            tone={getPlayerAvatarTone(avatarGame, player.playerId)}
                          />
                        )}
                        <span className="player-name">
                          {player.characterName || 'Unknown Character'}
                        </span>
                        {/* Discord status */}
                        {player.discordUsername ? (
                          <span className="text-slate fs-sm">🔗 {player.discordUsername}</span>
                        ) : (
                          <span className="text-slate fs-sm">No Discord</span>
                        )}
                      </div>
                      {/* FPR registration data */}
                      {isFprGame && (
                        <div className="d-flex align-items-center gap-2xs mt-2xs flex-wrap">
                          {player.fprRegistration ? (
                            <>
                              {player.fprRegistration.roles.map(role => (
                                <span key={role} className={`badge d-flex align-items-center gap-2xs ${role === 'tank' ? 'badge--aqua' : role === 'healer' ? 'badge--green' : ''}`}>
                                  <img src={getFprRoleIconUrl(role)} alt={role} style={{ width: 16, height: 16, objectFit: 'contain' }} />
                                  {role === 'tank' ? 'T' : role === 'healer' ? 'H' : 'D'}
                                </span>
                              ))}
                              {player.fprRegistration.availableAsLeader && (
                                <span className="badge badge--amber" title="Available as Team Leader">👑 TL</span>
                              )}
                              {player.fprRegistration.knowsMmMechanics && (
                                <span className="badge badge--blue" title="Knows MM Mechanics">⚙ MM</span>
                              )}
                              {player.fprRegistrationManual && (
                                <span className="text-slate fs-xs" title="Entered manually by admin">✏</span>
                              )}
                              <button
                                className="btn btn--outline btn--slate"
                                style={{ padding: '1px 6px', fontSize: '0.75rem' }}
                                onClick={() => onEditRegistration(player)}
                                title="Edit registration"
                              >
                                Edit
                              </button>
                            </>
                          ) : player.fprPendingDiscordLink ? (
                            <>
                              <span className="badge badge--amber" title="Player claims they registered via Discord — link their Discord account in the Discord tab">🔔 Claimed Discord Reg</span>
                              <button
                                className="btn"
                                style={{ padding: '1px 6px', fontSize: '0.75rem' }}
                                onClick={() => onEditRegistration(player)}
                              >
                                Fill In
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="badge badge--red" title="Missing registration">⚠ No Registration</span>
                              <button
                                className="btn"
                                style={{ padding: '1px 6px', fontSize: '0.75rem' }}
                                onClick={() => onEditRegistration(player)}
                              >
                                Fill In
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="player-actions">
                      <label className="check" title={player.enabled ? 'Disable player' : 'Enable player'}>
                        <input
                          className="switch"
                          type="checkbox"
                          checked={player.enabled}
                          onChange={() => togglePlayer(player.playerId, !player.enabled)}
                        />
                      </label>
                      <button
                        onClick={() => banPlayer(player.playerId)}
                        className="btn btn--sm btn--outline btn--red"
                        title="Ban player"
                      >
                        ⛔
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="d-flex flex-column gap-xs list list--divided">
              {bannedPlayers.length === 0 ? (
                <p className="empty-message">No banned players.</p>
              ) : (
                bannedPlayers.map((player) => (
                  <div key={player.playerId} className="list__item banned">
                    <div className="player-info">
                      <span className="player-name">
                        {player.characterName || 'Unknown Character'}
                      </span>
                      {player.reason && (
                        <span className="fs-sm text-red">Reason: {player.reason}</span>
                      )}
                    </div>
                    <div className="player-actions">
                      <button
                        onClick={() => unbanPlayer(player.playerId)}
                        className="btn btn--sm btn--green">
                        Unban
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
